import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Approach", href: "#approach" },
  { label: "Locations", href: "#locations" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 w-full z-50 border-b transition-colors duration-500 ${
        scrolled ? "bg-[#050505]/85 backdrop-blur-xl border-[#27272a]" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-5 md:px-10 h-20 flex items-center justify-between">
        <button data-testid="logo-home" onClick={() => go("#top")} className="flex items-center gap-3 group">
          <img src="/qualiz-logo.png" alt="Qualiz Material Testing & Research Centre logo" className="h-11 w-11 object-contain transition-transform duration-500 group-hover:scale-110" />
          <span className="font-heading font-black text-lg tracking-tighter uppercase">Qualiz</span>
          <span className="hidden sm:block font-mono text-[10px] tracking-[0.25em] text-[#8e8e93] uppercase border-l border-[#27272a] pl-3">
            NABL Lab
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#8e8e93] hover:text-[#f4f4f5] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="header-cta"
            onClick={() => go("#contact")}
            className="font-mono text-[11px] tracking-[0.2em] uppercase bg-[#ff4500] text-[#050505] px-5 py-2.5 hover:bg-[#f4f4f5] transition-colors duration-300"
          >
            Get a Quote
          </button>
        </nav>

        <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className="md:hidden text-[#f4f4f5]">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#050505] border-t border-[#27272a]"
          >
            <div className="flex flex-col px-5 py-6 gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left font-heading font-bold text-2xl uppercase tracking-tight"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="font-mono text-xs tracking-[0.2em] uppercase bg-[#ff4500] text-[#050505] px-5 py-3 mt-2"
              >
                Get a Quote
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
